'use strict';

const Action = require('./Action');

/**
 * @typedef {object} ExportedSetValueAction
 *
 * @property {string} func Nombre de la función: setValue/setDefaultValue/setFormula
 * @property {Array}  args Argumentos de entrada a la función:
 *                         - si el modo es valor por defecto / limpiar valor:
 *                             - el ID del campo
 *                         - si el modo es valor fijo:
 *                             - el ID del campo
 *                             - el valor fijo
 *                             - la propiedad extra "cleanup" si no se cumplen las condiciones
 *                         - si el modo es fórmula:
 *                             - el ID del campo
 *                             - la expresión de la fórmula a evaluar
 *                             - la propiedad extra "cleanup" si no se cumplen las condiciones
 */

const MODE = {
    DEFAULT: 'default',
    VALUE: 'value',
    FORMULA: 'formula',
    CLEAR: 'clear',
};

/**
 * Define function name
 *
 * @param {string} mode Mode
 *
 * @returns {string} Function name
 */
function defineFunction(mode) {
    let functionName;
    switch (mode) {
        case MODE.DEFAULT:
            functionName = 'setDefaultValue';
            break;

        case MODE.CLEAR:
            functionName = 'clearValue';
            break;

        case MODE.VALUE:
            functionName = 'setValue';
            break;

        case MODE.FORMULA:
            functionName = 'setFormula';
            break;
    }

    return functionName;
}

/**
 * Clase de gestión de una regla de tipo Establecer un valor en una variable
 *
 * @extends {Structure.Action}
 *
 * @memberOf Structure
 */
class SetValueAction extends Action {
    /**
     * Defined modes
     *
     * @type {Object}
     */
    static get MODE() {
        return MODE;
    }

    /**
     * @inheritDoc
     */
    constructor(rawAction) {
        super(rawAction);

        this.type = Action.Types.SET_VALUE;
        this.mode = rawAction.mode || MODE.DEFAULT;
        this.function = defineFunction(this.mode);

        this.value = rawAction.value;
        this.cleanup = !!rawAction.cleanup;
        this.fieldsInFormula = rawAction.fieldsInFormula || [];
    }

    /**
     * Get mode
     *
     * @return {String} Mode
     */
    getMode() {
        return this.mode;
    }

    /**
     * Get value
     *
     * @return {mixed} Value
     */
    getValue() {
        return this.value;
    }

    /**
     * Determina si el modo de valor es de fórmula
     *
     * @return {boolean} Si está definido como modo fórmula
     */
    hasFormula() {
        return this.getMode() === MODE.FORMULA;
    }

    /**
     * Determina si el elemento del CRF seleccionado participa en el resultado de la fórmula
     *
     * @param  {Number}  elementId ID único del elemento
     *
     * @return {boolean}           Si se incluye en la fórmula alguna expresión para recoger los datos del elemento
     */
    hasElementInFormula(elementId) {
        // La única opción posible actualmente es el valor de un campo
        // Si contiene field(elementId) o field(elementId, ...)
        const regex = new RegExp(`field\\(${elementId}(?:\\)|,)`);

        return this.hasFormula() && regex.test(this.value);
    }

    /**
     * Exportación de la acción en un objeto para la ejecución de las reglas
     *
     * @return {ExportedSetValueAction} Objeto de exportación de la acción
     */
    export() {
        const action = super.export();
        action.mode = this.mode;
        action.value = this.value;
        action.cleanup = this.cleanup;
        if (this.hasFormula()) {
            action.fieldsInFormula = this.fieldsInFormula;
        }

        return action;
    }
}

module.exports = SetValueAction;
